/**
 * Form validation and interactions.
 */

const HOPE_NAMED_VALUE = 'Share with my name';
const HOPE_SHARING_LABEL = 'Sharing…';

function isHopeForm(form) {
	return Boolean(form.querySelector('[name="hope-message"]'));
}

function hopeSubmitLabel(btn, label) {
	if (btn.tagName === 'INPUT') {
		btn.value = label;
		return;
	}
	btn.textContent = label;
}

function placeHopeSpinner(btn) {
	const host = btn.parentElement;
	if (!host || host.querySelector('.bts-hope-submit-spinner')) return;

	if (getComputedStyle(host).position === 'static') {
		host.style.position = 'relative';
	}

	const spinner = document.createElement('span');
	spinner.className = 'bts-hope-submit-spinner';
	spinner.setAttribute('aria-hidden', 'true');
	host.appendChild(spinner);

	const hostRect = host.getBoundingClientRect();
	const btnRect = btn.getBoundingClientRect();
	spinner.style.left = `${btnRect.left - hostRect.left + 16}px`;
	spinner.style.top = `${btnRect.top - hostRect.top + (btnRect.height - 16) / 2}px`;
}

function setHopeSubmitState(form, busy) {
	if (!isHopeForm(form)) return;

	form.querySelectorAll('.wpcf7-submit').forEach((btn) => {
		if (busy) {
			if (btn.dataset.btsSharing === '1') return;

			const width = btn.getBoundingClientRect().width;
			btn.dataset.btsSharing = '1';
			btn.dataset.btsLabel = btn.tagName === 'INPUT' ? btn.value : btn.textContent;
			btn.style.minWidth = `${Math.ceil(width)}px`;
			hopeSubmitLabel(btn, HOPE_SHARING_LABEL);
			btn.classList.add('is-sharing');
			btn.setAttribute('aria-busy', 'true');
			btn.disabled = true;
			placeHopeSpinner(btn);
			return;
		}

		if (btn.dataset.btsSharing !== '1') return;

		btn.classList.remove('is-sharing');
		btn.removeAttribute('aria-busy');
		btn.disabled = false;
		if (btn.dataset.btsLabel) {
			hopeSubmitLabel(btn, btn.dataset.btsLabel);
		}
		btn.style.minWidth = '';
		delete btn.dataset.btsLabel;
		delete btn.dataset.btsSharing;
		const host = btn.parentElement;
		if (host) {
			host.querySelectorAll('.bts-hope-submit-spinner').forEach((spinner) => spinner.remove());
		}
	});
}

function hopeShareIsNamed(form) {
	const selected = form.querySelector('input[name="hope-visibility"]:checked');
	return Boolean(selected && selected.value === HOPE_NAMED_VALUE);
}

function clearHopeFieldError(form, fieldName) {
	const wrap = form.querySelector(`.wpcf7-form-control-wrap[data-name="${fieldName}"]`);
	if (!wrap) return;

	wrap.querySelectorAll('.wpcf7-not-valid-tip').forEach((tip) => tip.remove());
	wrap.querySelectorAll('.wpcf7-form-control').forEach((control) => {
		control.classList.remove('wpcf7-not-valid');
		if (control.getAttribute('aria-invalid') === 'true') {
			control.setAttribute('aria-invalid', 'false');
		}
		if (typeof control.setCustomValidity === 'function') {
			control.setCustomValidity('');
		}
	});
}

function dismissHopeErrorResponse(form) {
	const status = form.getAttribute('data-status') || '';

	// Contact Form 7 keeps the thank-you text in this same box after a
	// successful send. Only an error response may be cleared.
	if (status === 'sent' || form.classList.contains('sent')) return;
	if (status !== 'invalid' && !form.classList.contains('invalid')) return;
	if (form.querySelector('.wpcf7-not-valid-tip')) return;

	form.classList.remove('invalid');
	form.setAttribute('data-status', 'init');
	form.querySelectorAll('.wpcf7-response-output').forEach((output) => {
		output.textContent = '';
	});
}

function syncHopeNameChoice(form) {
	if (!isHopeForm(form) || !form.querySelector('input[name="hope-visibility"]')) return;

	const nameInput = form.querySelector('input[name="hope-name"]');
	const photoInput = form.querySelector('input[name="hope-photo"]');
	if (!nameInput && !photoInput) return;

	if (hopeShareIsNamed(form)) return;

	// Anonymous notes use the wall placeholder. Drop a typed name and any
	// photo so neither hidden field can fail the next submit.
	if (nameInput) nameInput.value = '';
	if (photoInput) photoInput.value = '';
	clearHopeFieldError(form, 'hope-name');
	clearHopeFieldError(form, 'hope-photo');
	dismissHopeErrorResponse(form);
}

/**
 * Leave hope-photo out of this submit unless a named share has a real file.
 *
 * Contact Form 7 still type-checks an empty file input. Anonymous shares,
 * and named shares with no photo, must not send that empty input. The wall
 * shows the placeholder when no photo was saved.
 *
 * @param {HTMLFormElement} form
 */
function omitUnusedHopePhoto(form) {
	const photoInput = form.querySelector('input[name="hope-photo"]');
	if (!photoInput || !isHopeForm(form)) return;

	if (!hopeShareIsNamed(form)) {
		photoInput.value = '';
	}

	const hasFile = Boolean(photoInput.files && photoInput.files.length > 0);
	photoInput.disabled = !hopeShareIsNamed(form) || !hasFile;
}

function restoreHopePhoto(form) {
	const photoInput = form.querySelector('input[name="hope-photo"]');
	if (photoInput) photoInput.disabled = false;
}

function selectedHopeVisibility(form) {
	const selected = form.querySelector('input[name="hope-visibility"]:checked');
	return selected ? selected.value : '';
}

function rememberHopeVisibility(form) {
	if (!isHopeForm(form)) return;
	const value = selectedHopeVisibility(form);
	if (value) form.dataset.btsHopeVisibility = value;
}

function forgetHopeVisibility(form) {
	delete form.dataset.btsHopeVisibility;
}

function restoreRememberedHopeVisibility(form) {
	const value = form.dataset.btsHopeVisibility;
	if (!value) return;
	forgetHopeVisibility(form);

	const radio = Array.from(form.querySelectorAll('input[name="hope-visibility"]')).find(
		(input) => input.value === value
	);
	if (!radio || radio.checked) return;
	radio.checked = true;
}

function initHopeNameChoice() {
	document.querySelectorAll('form.wpcf7-form').forEach((form) => {
		form.querySelectorAll('input[name="hope-visibility"]').forEach((radio) => {
			radio.addEventListener('change', () => syncHopeNameChoice(form));
		});

		// Contact Form 7 calls form.reset() after a successful send. The reset
		// event fires before the browser restores the default radio, which is
		// Share anonymously. Put the submitted choice back once that is done.
		form.addEventListener('reset', () => {
			if (!form.dataset.btsHopeVisibility) return;
			queueMicrotask(() => restoreRememberedHopeVisibility(form));
		});
	});
}

export default function initForms() {
	initHopeNameChoice();

	document.addEventListener(
		'submit',
		(event) => {
			const form = event.target;
			if (!(form instanceof HTMLFormElement)) return;
			if (!form.classList.contains('wpcf7-form')) return;
			syncHopeNameChoice(form);
			omitUnusedHopePhoto(form);
			const busy =
				form.dataset.btsSubmitting === '1' ||
				form.getAttribute('data-status') === 'submitting';
			if (busy) {
				restoreHopePhoto(form);
				event.preventDefault();
				event.stopImmediatePropagation();
				return;
			}
			rememberHopeVisibility(form);
			form.dataset.btsSubmitting = '1';
			if (!isHopeForm(form)) {
				form.querySelectorAll('.wpcf7-submit').forEach((btn) => {
					btn.disabled = true;
				});
			}
		},
		true // capture: before CF7's bubble listener
	);
	const unlock = (event) => {
		const form = event.target;
		if (!(form instanceof HTMLFormElement)) return;
		delete form.dataset.btsSubmitting;
		if (event.type !== 'wpcf7mailsent' && event.type !== 'wpcf7submit') {
			forgetHopeVisibility(form);
		}
		restoreHopePhoto(form);
		form.querySelectorAll('.wpcf7-submit').forEach((btn) => {
			btn.disabled = false;
		});
		setHopeSubmitState(form, false);
	};
	[
		'wpcf7invalid',
		'wpcf7spam',
		'wpcf7mailsent',
		'wpcf7mailfailed',
		'wpcf7submit',
	].forEach((evt) => document.addEventListener(evt, unlock));
	document.addEventListener('wpcf7beforesubmit', (event) => {
		const form = event.target;
		if (!(form instanceof HTMLFormElement)) return;
		// FormData is already built. Turn the file control back on so a later
		// named share can still choose a photo.
		restoreHopePhoto(form);
		setHopeSubmitState(form, true);
	});
	// Safety net if two responses already raced
	document.addEventListener('wpcf7invalid', (event) => {
		event.target
			.querySelectorAll('.wpcf7-form-control-wrap')
			.forEach((wrap) => {
				wrap.querySelectorAll('.wpcf7-not-valid-tip').forEach((tip, i) => {
					if (i > 0) tip.remove();
				});
			});
	});
}
document.addEventListener('DOMContentLoaded', function () {
	const textarea = document.querySelector('textarea[name="hope-message"]');
	if (!textarea) return;

	const max = 300;
	textarea.maxLength = max;

	if (textarea.placeholder && /200 words/i.test(textarea.placeholder)) {
		textarea.placeholder = textarea.placeholder.replace(/200 words/gi, '300 characters');
	}

	const limit = () => {
		if (textarea.value.length > max) {
			textarea.value = textarea.value.slice(0, max);
		}
	};

	textarea.addEventListener('input', limit);
	limit();
});